// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import reset from "@insite/client-framework/Store/Components/ProductSelector/Handlers/Reset";
import searchProducts from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SearchProducts";
import setProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetProduct";
import setUnitOfMeasure from "@insite/client-framework/Store/Components/ProductSelector/Handlers/SetUnitOfMeasure";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { DynamicDropdownPresentationProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps, IconProps } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Modal from "@insite/mobius/Modal";
import { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngLoadingOverlay from "@pace/App/Components/Common/XngLoadingOverlay";
import Notify from "@pace/App/Components/Notify";
import ProductBarcodeScanner from "@pace/App/Components/ProductBarcodeScanner/ProductBarcodeScanner";
import { ScannerStyles } from "@pace/App/Components/ProductBarcodeScanner/scanner.styles";
import { CustomAppState } from "@pace/App/Store/Reducers";
import DynamicDropdown from "@pace/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import debounce from "lodash/debounce";
import React, { ChangeEvent, Dispatch, ReactNode, useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    onSelectProduct: (productInfo: ProductInfo, product: ProductModel) => void;
    skipInventoryValidation?: boolean;
    denyFixedConfiguration?: boolean;
    selectButtonTitle?: string;
    productIsConfigurableMessage?: ReactNode;
    productIsUnavailableMessage?: ReactNode;
    customErrorMessage?: ReactNode;
    extendedStyles?: ProductSelectorStyles;
    // XNG-Changes
    skipAddToCartValidation?: boolean;
    isQuickOrder?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const { isSearching, searchResults, selectedProductInfo, errorType } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value;
    return {
        isSearching,
        searchResults,
        selectedProductInfo,
        product,
        errorType,
        location: getLocation(state),
        // Xng-Changes
        isScannedProductModalOpen: state.xQuickOrder.isScannedProductModalOpen,
        productInfos: state.pages.quickOrder.productInfos,
    };
};

const mapDispatchToProps = {
    searchProducts,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    reset,
    // Xng-Change
    loadProduct,
    setIsScannedProductModalOpen: (isOpen: boolean) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xQuickOrder/SetIsScannedProductModalOpen",
            isOpen
        } as any);
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSelectorStyles {
    container?: GridContainerProps;
    searchGridItem?: GridItemProps;
    qtyGridItem?: GridItemProps;
    unitOfMeasureGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    qtyTextField?: TextFieldPresentationProps;
    optionWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    infoWrapper?: InjectableCss;
    autocompleteTitleText?: TypographyPresentationProps;
    autocompleteErpText?: TypographyPresentationProps;
    selectButton?: ButtonPresentationProps;
    searchDynamicDropdown?: DynamicDropdownPresentationProps;
    clearIcon?: IconProps;
    // XNG-Changes
    scannerStyles?: ScannerStyles;
    addButtonWrapper?: InjectableCss;
    scannedProductWrapper?: InjectableCss;
    productDescriptionStyles?: ProductDescriptionStyles;
    productImageStyles?: ProductImageStyles;
    existingItemMessage?: InjectableCss;
    searchWrapper?: InjectableCss;
    clearSelectedProductIcon?: IconPresentationProps;
}

export const productSelectorStyles: ProductSelectorStyles = {
    container: {
        gap: 10,
    },
    searchGridItem: {
        width: [12, 12, 4, 5, 5],
        css: css`
            position: relative;
        `,
    },
    qtyGridItem: {
        width: [3, 3, 2, 1, 1],
    },
    unitOfMeasureGridItem: {
        width: [9, 9, 3, 3, 3],
    },
    buttonGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    optionWrapper: {
        css: css`
            display: flex;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            width: 50px;
            height: 50px;
            margin-right: 10px;
        `,
    },
    productImage: {
        width: "50px",
        css: css`
            flex-shrink: 0;
            img {
                height: 100%;
            }
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    autocompleteTitleText: {
        size: 14,
    },
    autocompleteErpText: {
        size: 14,
        css: css`
            margin-top: 5px;
        `,
    },
    selectButton: {
        css: css`
            margin-top: 30px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    clearIcon: {
        css: css`
            position: absolute;
            right: 40px;
            top: 45px;
            cursor: pointer;
        `,
    },
};

const ENTER_KEY = 13;

// Xng-Change
const debouncedSearch = debounce(
    (searchProducts, searchText: string, denyFixedConfiguration?: boolean) => {
        searchProducts({ query: searchText, denyProductId: denyFixedConfiguration ? undefined : undefined });
    },
    300
);

const ProductSelector = ({
    onSelectProduct,
    skipInventoryValidation,
    denyFixedConfiguration,
    selectButtonTitle,
    searchProducts,
    isSearching,
    searchResults,
    setProduct,
    clearProduct,
    setUnitOfMeasure,
    selectedProductInfo,
    product,
    errorType,
    extendedStyles,
    customErrorMessage,
    productIsConfigurableMessage,
    productIsUnavailableMessage,
    location,
    reset,
    // XNG-Changes
    skipAddToCartValidation,
    isQuickOrder,
    loadProduct,
    isScannedProductModalOpen,
    setIsScannedProductModalOpen,
    productInfos,
}: Props) => {
    const [qty, setQty] = useState("1");
    const [qtyHasChanged, setQtyHasChanged] = useState(false);
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [selectedProductId, setSelectedProductId] = useState("");
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [styles] = useState(() => mergeToNew(productSelectorStyles, extendedStyles));
    // XNG-Changes:
    const [showRemoveIcon, setShowRemoveIcon] = useState(false);
    const [scannedProduct, setScannedProduct] = useState<ProductModel>();
    const [scannedProductContext, setScannedProductContext] = useState<ProductContextModel>();
    // XNG-Changes:
    const dropdownRef = React.createRef<HTMLInputElement>();
    const [isScannerSearch, setIsScannerSearch] = useState(false);
    const qtyRef = React.createRef<HTMLInputElement>();

    useEffect(() => {
        reset();
    }, [location.pathname]);

    useEffect(() => {
        if (!searchResults) {
            return;
        }

        const newOptions = searchResults.map(product => ({
            optionText: product.title,
            optionValue: product.id || undefined,
            rowChildren: (
                <StyledWrapper {...styles.optionWrapper} data-test-selector={`productSelector_search_${product.id}`}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage {...styles.productImage} src={product.image} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.infoWrapper}>
                        <Typography {...styles.autocompleteTitleText} data-test-selector="productSelector_ProductTitle">
                            {product.displayTitle || product.title}
                        </Typography>
                        <Typography {...styles.autocompleteErpText}>
                            {product.displayErpNumber || product.erpNumber}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ),
        }));
        setOptions(newOptions);
    }, [searchResults]);

    /**
     * Xng-Change PAC-1251
     * if it is an exact match having only one option using barcode scanner
     * select that item
     * added setTimeout with 100ms to make sure options got rendered to the DOM
     * we can't just call onSelectionChangeHandler as DynamicDropdown will not populate the option text
     * and will not close the popover as Pace requested
     * after selecting item focus on Qty field
     */
    useEffect(() => {
        if (isScannerSearch) {
            setTimeout(() => {
                dropdownRef.current?.click();
            }, 200);
        }
        if(!selectedProductInfo && isScannerSearch) {
            setTimeout(() => {
                const product = document.querySelector("ul[data-test-selector='productSelector_search-listbox'] > div");
                if(product) {
                    (product as HTMLElement)?.click();
                }
            }, 400);
        }
    }, [options, searchResults]);

    useEffect(() => {
        setErrorMessage(customErrorMessage);
    }, [customErrorMessage]);

    useEffect(() => {
        setSelectedProductId(selectedProductInfo ? selectedProductInfo.productId : "");
        // Xng-Change
        if (selectedProductInfo && !qtyHasChanged && !isScannerSearch) {
            setQty(`${selectedProductInfo.qtyOrdered}`);
        }
        if(selectedProductInfo && isQuickOrder && !isScannerSearch) {
            selectProduct();
        }
        setScannedProductContext({
            product: scannedProduct as ProductModel,
            productInfo: selectedProductInfo as ProductInfo,
        });
    }, [selectedProductInfo]);

    useEffect(() => {
        switch (errorType) {
            case "productIsConfigurable":
                setErrorMessage(productIsConfigurableMessage || translate("Cannot select configurable products"));
                break;
            case "productIsUnavailable":
                setErrorMessage(productIsUnavailableMessage || translate("Product is unavailable"));
                break;
            default:
                setErrorMessage("");
                break;
        }
        
        // XNG-Changes: PAC-732
        if (skipAddToCartValidation && errorType === "productIsUnavailable") {
            setErrorMessage("");
        }
    }, [errorType]);

    const onSelectionChangeHandler = (value?: string) => {
        if (searchResults && searchResults.length > 0) {
            const variantParentId = searchResults.find(o => o.id === value)?.styleParentId;
            if (variantParentId) {
                setProduct({
                    productId: variantParentId,
                    variantId: value,
                    validateProduct: true,
                    skipInventoryValidation,
                    denyFixedConfiguration,
                });
                return;
            }
            // Xng-Change
            if(isScannerSearch && value) {
                loadProduct({
                    id: value,
                    onSuccess: (product: ProductModel) => {
                        setScannedProduct(product);
                        setIsScannedProductModalOpen(true);
                        setQty("");
                    },
                });
            }
            setProduct({
                productId: value,
                validateProduct: true,
                skipInventoryValidation,
                denyFixedConfiguration,
            });
        }
    };

    const debouncedSearchProducts = debounce((query: string) => {
        searchProducts({ query });
    }, 300);

    // Xng-Change useCallBack
    const onInputChanged = useCallback(
        (event: ChangeEvent<HTMLInputElement>, isScanner = false) => {
            setErrorMessage("");
            if(!showRemoveIcon) {
                setShowRemoveIcon(true);
            }
            // XNG-Changes
            const value = event.target.value;
            if (isScanner) {
                dropdownRef.current?.click();
            }

            setSearchTerm(event.target.value);

            if (selectedProductInfo) {
                clearProduct();
            }

            if (value) {
                debouncedSearch(searchProducts, value, denyFixedConfiguration);
            }
        },
        [searchProducts, clearProduct, denyFixedConfiguration, showRemoveIcon]
    );

    const selectProduct = () => {
        if (!selectedProductInfo || errorMessage || !product) {
            return;
        }

        const newProductInfo = { ...selectedProductInfo };
        newProductInfo.qtyOrdered = parseFloat(qty);

        onSelectProduct(newProductInfo, product);

        clearProduct();
        setQty("1");
        setQtyHasChanged(false);
        searchProducts({ query: "" });
        // XNG-Changes:
        setShowRemoveIcon(false);
        setIsScannedProductModalOpen(false);
    };

    const uomChangeHandler = (unitOfMeasure: string) => {
        if (!selectedProductInfo) {
            return;
        }

        setUnitOfMeasure({ unitOfMeasure });
    };

    const onKeyPress = (event: React.KeyboardEvent) => {
        if (event.charCode === ENTER_KEY) {
            if (selectedProductInfo) {
                selectProduct();
            } else if (searchTerm && (!searchResults || searchResults.length === 0)) {
                setProduct({ searchTerm, skipInventoryValidation, denyFixedConfiguration });
            }
        }
        if(isScannerSearch) {
            setIsScannerSearch(false);
        }
    };

    const clearButtonHandler = (component?: "autocomplete-text" | "qty") => {
        if(component === "qty") {
            setQty("");
            return;
        }
        clearProduct();
        setQtyHasChanged(false);
        searchProducts({ query: "" });
        setShowRemoveIcon(false);
        setSearchTerm("");
    };

    const searchByScanner = (search: string) => {
        setIsScannerSearch(true);
        onInputChanged({ target: { value: search } } as any, true);
        // Here come from scanner
    };

    // Xng-Change
    const handleCloseScannedProductModal = () => {
        setIsScannedProductModalOpen(false);
        setIsScannerSearch(false);
        clearProduct();
    };

    // Xng-Change
    const addProduct = () => {
        if(!scannedProduct) {
            return;
        }
        selectProduct();
        setTimeout(() => {
            if(isScannerSearch) {
                document.getElementById("quickOrder-searchByBarcode")?.click();
                setIsScannerSearch(false);
            }
        }, 200);
    };

    const isSelectedProductAlreadyInAdded = () => {
        return scannedProductContext?.productInfo && productInfos.length > 0 && productInfos.findIndex(p => p.productId === scannedProductContext?.productInfo.productId) > -1;
    };

    // Xng-Change
    // Change structure and add LoadingOverlay
    return (
        <GridContainer {...styles.container} data-test-selector="productSelector">
            <XngLoadingOverlay id="quickOrder" loading={(!isScannerSearch && isScannedProductModalOpen) || (isScannerSearch && !isScannedProductModalOpen)}>
                <GridItem {...styles.searchGridItem}>
                    <DynamicDropdown
                        {...styles.searchDynamicDropdown}
                        label={translate("Search")}
                        onSelectionChange={onSelectionChangeHandler}
                        onInputChange={onInputChanged}
                        onKeyPress={onKeyPress}
                        filterOption={() => true}
                        selected={selectedProductId}
                        isLoading={isSearching}
                        options={options}
                        error={errorMessage}
                        data-test-selector="productSelector_search"
                        placeholder={translate("Type to search")}
                        // XNG-Changes
                        {...{ inputRef: dropdownRef, search: searchTerm }}
                        hidePopover={isScannedProductModalOpen}
                    />
                    {/* XNG-Changes: PAC-333 */}
                    <Hidden above="md">
                        <ProductBarcodeScanner
                            extendedStyles={styles.scannerStyles}
                            onSearch={(result: string) => searchByScanner(result)}
                            shouldSearchImmediately={isQuickOrder}
                            onClose={() => setIsScannerSearch(false)}
                            elementId="quickOrder-searchByBarcode"
                        />
                    </Hidden>
                    {/* XNG-Changes: */}
                    {(showRemoveIcon || selectedProductInfo) && (
                        <Icon {...styles.clearIcon} src={X} onClick={() => clearButtonHandler("autocomplete-text")} className="autocomplete-clear-icon" />
                    )}
                </GridItem>
                <GridItem {...styles.qtyGridItem}>
                    <TextField
                        type="qty"
                        min="0"
                        label={translate("QTY")}
                        value={qty}
                        onChange={e => {
                            setQty(e.currentTarget.value);
                            setQtyHasChanged(true);
                        }}
                        data-test-selector="productSelector_qty"
                        ref={qtyRef}
                    />
                    <Icon {...styles.clearIcon} src={X} onClick={() => clearButtonHandler("qty")} />
                </GridItem>
                <GridItem {...styles.unitOfMeasureGridItem}>
                    {product && selectedProductInfo && (
                        <ProductContext.Provider
                            value={{ product, productInfo: selectedProductInfo, onUnitOfMeasureChanged: uomChangeHandler }}
                        >
                            <ProductUnitOfMeasureSelect
                                extendedStyles={styles.unitOfMeasureSelect}
                                data-test-selector="productSelector_uom"
                            />
                        </ProductContext.Provider>
                    )}
                </GridItem>
                {/* Xng-Change: hide button for quick order */}
                {!isQuickOrder &&
                    <GridItem {...styles.buttonGridItem}>
                        <Button
                            {...styles.selectButton}
                            onClick={() => selectProduct()}
                            // XNG-Changes:
                            disabled={!selectedProductInfo || !showRemoveIcon || !!errorMessage || Number(qty) < 1}
                            data-test-selector="productSelector_selectProduct"
                        >
                            {selectButtonTitle || translate("Select Product")}
                        </Button>
                    </GridItem>
                }
            </XngLoadingOverlay>
            {/* Xng-Change */}
            <Modal
                isOpen={isScannedProductModalOpen}
                closeOnEsc={true}
                handleClose={handleCloseScannedProductModal}
                headline={translate("Select Quantity")}
                sizeVariant="medium"
            >
                <StyledWrapper {...styles.searchWrapper}>
                    <TextField
                        type="qty"
                        min="0"
                        label={scannedProduct?.productNumber}
                        value={qty}
                        onChange={e => {
                            setQty(e.currentTarget.value);
                            setQtyHasChanged(true);
                        }}
                        data-test-selector="productSelector_qty"
                        id="productSelector_qty"
                        autoFocus />
                    <Icon onClick={() => clearButtonHandler("qty")} {...styles.clearSelectedProductIcon} />
                </StyledWrapper>
                {isSelectedProductAlreadyInAdded() &&
                    <Notify type="info" {...styles.existingItemMessage}>
                        {siteMessage("Custom_Select_Quantity_Existing_Item")}
                    </Notify>
                }
                <StyledWrapper {...styles.addButtonWrapper}>
                    <Button onClick={addProduct} disabled={!qty}>{translate("Add")}</Button>
                </StyledWrapper>
                {scannedProductContext?.product && scannedProductContext?.productInfo &&
                    <ProductContext.Provider
                        value={scannedProductContext}
                    >
                        <StyledWrapper {...styles.scannedProductWrapper}>
                            <ProductImage product={scannedProductContext} extendedStyles={styles.productImageStyles} />
                            <ProductDescription product={scannedProductContext} extendedStyles={styles.productDescriptionStyles} />
                        </StyledWrapper>
                    </ProductContext.Provider>
                }
            </Modal>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductSelector);
